import React from "react";
import { motion } from "framer-motion";
import { Quote, Award, Heart, MapPin, Calendar, Globe2 } from "lucide-react";
import { BRAND } from "../data/site";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Decorative blurs */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-brand-orange/10 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-ocean/10 rounded-full blur-3xl -z-10" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left visual stack */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl">
              <img
                src="https://images.pexels.com/photos/9400914/pexels-photo-9400914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Travel inspiration"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/55 via-transparent to-transparent" />
            </div>

            {/* Floating chip — owner badge with FOUNDER'S NOTE label */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -left-4 sm:-left-8 glass rounded-2xl p-5 max-w-[270px] shadow-2xl"
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-brand-orange/15 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-brand-orange" />
                </div>
                <div>
                  <div className="text-[0.6rem] uppercase tracking-[0.2em] text-brand-orange font-semibold">
                    Founder's Note
                  </div>
                  <div className="font-playfair text-lg font-bold text-slate-900">
                    {BRAND.owner}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed italic">
                "Every traveller deserves a story worth retelling. That's what
                we set out to build — a holiday partner you can call anytime."
              </p>
            </motion.div>

            {/* Floating award chip */}
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -top-5 -right-3 sm:-right-6 glass-sand rounded-2xl px-5 py-4 shadow-xl"
            >
              <div className="flex items-center gap-3">
                <Award className="w-7 h-7 text-brand-orange" />
                <div>
                  <div className="font-playfair text-2xl font-bold text-slate-900 leading-none">
                    Est. {BRAND.established}
                  </div>
                  <div className="text-[0.7rem] uppercase tracking-[0.18em] text-slate-600 mt-1">
                    Trusted in Burhanpur
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="text-xs uppercase tracking-[0.24em] text-brand-orange font-semibold">
              About Us
            </div>
            <h2
              data-testid="about-heading"
              className="mt-3 font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight text-balance"
            >
              A small-town travel house with a{" "}
              <span className="italic text-brand-ocean">world-sized</span> heart.
            </h2>

            {/* Quick-scan info row */}
            <div className="mt-5 flex flex-wrap gap-3">
              {[
                { icon: MapPin, label: "Burhanpur, M.P." },
                { icon: Calendar, label: `Since ${BRAND.established}` },
                { icon: Globe2, label: "Family-run" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-ocean/8 border border-brand-ocean/15 text-sm text-brand-ocean font-medium"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-6 space-y-4 text-slate-700 text-[1.05rem] leading-relaxed">
              <p>
                Founded in {BRAND.established} in the historic town of Burhanpur,{" "}
                <span className="font-semibold text-slate-900">
                  Jayesh Tour Packages &amp; Travellers
                </span>{" "}
                — popularly known as Happy Journey 4U — is led by{" "}
                <span className="font-semibold text-brand-ocean">
                  {BRAND.owner}
                </span>
                . What began as a one-man counter helping neighbours plan
                weekend getaways has grown into a beloved family travel agency
                serving thousands across Madhya Pradesh and beyond.
              </p>
              <p>
                We don't just sell holidays — we listen first. Whether you're
                planning a honeymoon in Bali, a temple yatra across South India
                or your child's first international visa, every itinerary is
                shaped around <em>your</em> story, your budget and your pace.
              </p>
            </div>

            {/* Pillars — icon + title + subtitle cards */}
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {[
                {
                  icon: "🎯",
                  k: "Personalised",
                  v: "Plans built around you, not templates",
                },
                {
                  icon: "🤝",
                  k: "Trusted",
                  v: "Verified partners, honest pricing",
                },
                {
                  icon: "📞",
                  k: "Reachable",
                  v: "Direct line to the owner, always",
                },
              ].map((p) => (
                <div
                  key={p.k}
                  className="glass rounded-2xl p-5 hover:bg-white/85 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <span className="text-2xl">{p.icon}</span>
                  <div className="mt-3 font-playfair text-xl font-bold text-slate-900">
                    {p.k}
                  </div>
                  <div className="text-sm text-slate-600 mt-1 leading-snug">
                    {p.v}
                  </div>
                </div>
              ))}
            </div>

            {/* Full-service checklist */}
            <div className="mt-8 p-5 rounded-2xl bg-slate-50 border border-slate-100">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
                Everything under one roof
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {["✈️ Flights", "🏨 Hotels", "🛂 Visa", "🚌 Transport", "👥 Group Tours", "💍 Honeymoons"].map((item) => (
                  <span key={item} className="text-sm text-slate-700 font-medium">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
